import React, { useState, useEffect } from 'react';
import { 
  FaLock, 
  FaUnlock, 
  FaBuilding, 
  FaUserShield, 
  FaChalkboardTeacher, 
  FaGlobeAmericas, 
  FaTools, 
  FaHistory,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUser,
  FaSave,
  FaUndo
} from 'react-icons/fa';
import { API_BASE_URL } from '../../utils/apiConfig';

interface GlobalAccessSettings {
  blockAllUsers: boolean;
  blockAllUsersReason: string;
  blockAllUsersAt?: string;
  blockAllTeachers: boolean;
  blockAllTeachersReason: string;
  blockAllTeachersAt?: string;
  blockAllOrgAdmins: boolean;
  blockAllOrgAdminsReason: string;
  blockAllOrgAdminsAt?: string;
  globalBlockExpiresAt?: string;
  globalBlockMessage: string;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  maintenanceStartAt?: string;
  maintenanceEndAt?: string;
  lastModifiedBy?: string;
  lastModifiedAt?: string;
}

interface OrganizationAccessControl {
  _id: string;
  tenantKey: string;
  displayName: string;
  status: string;
  accessControl: {
    isBlocked: boolean;
    blockReason?: string;
    blockedAt?: string;
    blockedBy?: string;
    blockTeachers: boolean;
    blockTeachersReason?: string;
    blockAdmins: boolean;
    blockAdminsReason?: string;
    blockedUserIds: string[];
    blockExpiresAt?: string;
    blockMessage?: string;
  };
}

interface HistoryEntry {
  action: string;
  targetType: string;
  reason: string;
  performedBy: string;
  performedAt: string;
}

export const AccessControlPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'global' | 'organizations' | 'history'>('global');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Global settings state
  const [globalSettings, setGlobalSettings] = useState<GlobalAccessSettings>({
    blockAllUsers: false,
    blockAllUsersReason: '',
    blockAllTeachers: false,
    blockAllTeachersReason: '',
    blockAllOrgAdmins: false,
    blockAllOrgAdminsReason: '',
    globalBlockMessage: 'El sistema se encuentra temporalmente fuera de servicio.',
    maintenanceMode: false,
    maintenanceMessage: 'El sistema está en mantenimiento programado.',
  });
  
  // Organizations state
  const [organizations, setOrganizations] = useState<OrganizationAccessControl[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationAccessControl | null>(null);
  const [orgSettings, setOrgSettings] = useState<OrganizationAccessControl['accessControl'] | null>(null);
  
  // History state
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  
  // Bulk selection state
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [bulkReason, setBulkReason] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load global settings
      const globalResponse = await fetch(`${API_BASE_URL}/api/access-control/global`, {
        headers: getAuthHeaders()
      });
      
      if (globalResponse.ok) {
        const globalData = await globalResponse.json();
        if (globalData.success) {
          setGlobalSettings(globalData.settings);
        }
      }
      
      // Load organizations
      const orgsResponse = await fetch(`${API_BASE_URL}/api/access-control/organizations`, {
        headers: getAuthHeaders()
      });
      
      if (orgsResponse.ok) {
        const orgsData = await orgsResponse.json();
        if (orgsData.success) {
          setOrganizations(orgsData.organizations);
        }
      }
      
      // Load history
      const historyResponse = await fetch(`${API_BASE_URL}/api/access-control/global/history`, {
        headers: getAuthHeaders()
      });
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        if (historyData.success) {
          setHistory(historyData.history);
        }
      }
    } catch (err: any) {
      setError('Error cargando configuración de control de acceso');
      console.error('Error loading access control data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/access-control/global`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(globalSettings)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Configuración global guardada exitosamente');
        loadData(); // Reload to get updated history
      } else {
        setError(data.message || 'Error al guardar configuración');
      }
    } catch (err: any) {
      setError('Error al guardar configuración global');
    } finally {
      setSaving(false);
    }
  };

  const saveOrgSettings = async () => {
    if (!selectedOrg || !orgSettings) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/access-control/organization/${selectedOrg.tenantKey}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(orgSettings)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Configuración de ${selectedOrg.displayName} guardada`);
        loadData();
        setSelectedOrg(null);
        setOrgSettings(null);
      } else {
        setError(data.message || 'Error al guardar configuración');
      }
    } catch (err: any) {
      setError('Error al guardar configuración de organización');
    } finally {
      setSaving(false);
    }
  };

  const executeBulkAction = async () => {
    if (selectedOrgs.length === 0 || !bulkAction) return;
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/access-control/bulk-update`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          tenantKeys: selectedOrgs,
          action: bulkAction,
          accessControl: {
            blockReason: bulkReason,
            blockTeachersReason: bulkReason,
            blockAdminsReason: bulkReason
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Acción aplicada a ${data.modifiedCount} organizaciones`);
        loadData();
        setSelectedOrgs([]);
        setBulkAction('');
        setBulkReason('');
      } else {
        setError(data.message || 'Error al ejecutar acción masiva');
      }
    } catch (err: any) {
      setError('Error al ejecutar acción masiva');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-CL');
  };

  const getActionLabel = (action: string): string => {
    const labels: { [key: string]: string } = {
      'block_all_users': 'Bloqueó todos los usuarios',
      'unblock_all_users': 'Desbloqueó todos los usuarios',
      'block_all_teachers': 'Bloqueó todos los docentes',
      'unblock_all_teachers': 'Desbloqueó todos los docentes',
      'block_all_org_admins': 'Bloqueó administradores',
      'unblock_all_org_admins': 'Desbloqueó administradores',
      'enable_maintenance': 'Activó mantenimiento',
      'disable_maintenance': 'Desactivó mantenimiento'
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando control de acceso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <FaUserShield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Control de Acceso</h2>
              <p className="text-sm text-gray-600">
                Gestiona el acceso de usuarios a la plataforma
              </p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <FaUndo className="w-4 h-4" />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
          <div className="flex items-center">
            <FaTimesCircle className="text-red-500 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
          <div className="flex items-center">
            <FaCheckCircle className="text-green-500 mr-3" />
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('global')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'global'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FaGlobeAmericas className="w-4 h-4" />
                <span>Bloqueo Global</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('organizations')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'organizations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FaBuilding className="w-4 h-4" />
                <span>Por Establecimiento</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FaHistory className="w-4 h-4" />
                <span>Historial</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Global Settings Tab */}
          {activeTab === 'global' && (
            <div className="space-y-6">
              {/* Maintenance Mode */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <FaTools className="w-5 h-5 text-yellow-600" />
                    <div>
                      <h3 className="font-semibold text-yellow-800">Modo Mantenimiento</h3>
                      <p className="text-sm text-yellow-600">
                        Bloquea el acceso a todos los usuarios mostrando mensaje de mantenimiento
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={globalSettings.maintenanceMode}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        maintenanceMode: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-500"></div>
                  </label>
                </div>
                {globalSettings.maintenanceMode && (
                  <div className="space-y-3 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-yellow-800 mb-1">
                        Mensaje de mantenimiento
                      </label>
                      <textarea
                        value={globalSettings.maintenanceMessage}
                        onChange={(e) => setGlobalSettings({
                          ...globalSettings,
                          maintenanceMessage: e.target.value
                        })}
                        className="w-full p-3 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-yellow-800 mb-1">
                          Inicio
                        </label>
                        <input
                          type="datetime-local"
                          value={globalSettings.maintenanceStartAt?.slice(0, 16) || ''}
                          onChange={(e) => setGlobalSettings({
                            ...globalSettings,
                            maintenanceStartAt: e.target.value ? new Date(e.target.value).toISOString() : undefined
                          })}
                          className="w-full p-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-yellow-800 mb-1">
                          Fin
                        </label>
                        <input
                          type="datetime-local"
                          value={globalSettings.maintenanceEndAt?.slice(0, 16) || ''}
                          onChange={(e) => setGlobalSettings({
                            ...globalSettings,
                            maintenanceEndAt: e.target.value ? new Date(e.target.value).toISOString() : undefined
                          })}
                          className="w-full p-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Block All Users */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <FaLock className="w-5 h-5 text-red-600" />
                    <div>
                      <h3 className="font-semibold text-red-800">Bloquear Todos los Usuarios</h3>
                      <p className="text-sm text-red-600">
                        Bloquea el acceso a todos los usuarios de todos los establecimientos
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={globalSettings.blockAllUsers}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        blockAllUsers: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-500"></div>
                  </label>
                </div>
                {globalSettings.blockAllUsers && (
                  <div>
                    <label className="block text-sm font-medium text-red-800 mb-1">
                      Razón del bloqueo
                    </label>
                    <input
                      type="text"
                      value={globalSettings.blockAllUsersReason}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        blockAllUsersReason: e.target.value
                      })}
                      className="w-full p-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="Ej: Actualización del sistema"
                    />
                  </div>
                )}
              </div>

              {/* Block All Teachers */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <FaChalkboardTeacher className="w-5 h-5 text-orange-600" />
                    <div>
                      <h3 className="font-semibold text-orange-800">Bloquear Todos los Docentes</h3>
                      <p className="text-sm text-orange-600">
                        Bloquea el acceso a docentes de todos los establecimientos
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={globalSettings.blockAllTeachers}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        blockAllTeachers: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                {globalSettings.blockAllTeachers && (
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-1">
                      Razón del bloqueo
                    </label>
                    <input
                      type="text"
                      value={globalSettings.blockAllTeachersReason}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        blockAllTeachersReason: e.target.value
                      })}
                      className="w-full p-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      placeholder="Ej: Período de cierre de calificaciones"
                    />
                  </div>
                )}
              </div>

              {/* Block All Org Admins */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <FaUserShield className="w-5 h-5 text-purple-600" />
                    <div>
                      <h3 className="font-semibold text-purple-800">Bloquear Administradores de Establecimiento</h3>
                      <p className="text-sm text-purple-600">
                        Bloquea el acceso a administradores de todos los establecimientos
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={globalSettings.blockAllOrgAdmins}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        blockAllOrgAdmins: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-500"></div>
                  </label>
                </div>
                {globalSettings.blockAllOrgAdmins && (
                  <div>
                    <label className="block text-sm font-medium text-purple-800 mb-1">
                      Razón del bloqueo
                    </label>
                    <input
                      type="text"
                      value={globalSettings.blockAllOrgAdminsReason}
                      onChange={(e) => setGlobalSettings({
                        ...globalSettings,
                        blockAllOrgAdminsReason: e.target.value
                      })}
                      className="w-full p-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Ej: Actualización de permisos"
                    />
                  </div>
                )}
              </div>

              {/* Global Block Message */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Mensaje personalizado para usuarios bloqueados
                </label>
                <textarea
                  value={globalSettings.globalBlockMessage}
                  onChange={(e) => setGlobalSettings({
                    ...globalSettings,
                    globalBlockMessage: e.target.value
                  })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Este mensaje se mostrará a los usuarios bloqueados..."
                />
              </div>

              {/* Block Expiration */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <FaClock className="w-5 h-5 text-gray-600" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Expiración del Bloqueo</h3>
                    <p className="text-sm text-gray-600">
                      Los bloqueos se levantarán automáticamente en esta fecha
                    </p>
                  </div>
                </div>
                <input
                  type="datetime-local"
                  value={globalSettings.globalBlockExpiresAt?.slice(0, 16) || ''}
                  onChange={(e) => setGlobalSettings({
                    ...globalSettings,
                    globalBlockExpiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined
                  })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={saveGlobalSettings}
                  disabled={saving}
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />
                      <span>Guardar Configuración Global</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <div className="space-y-6">
              {/* Bulk Actions */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-4">Acciones Masivas</h3>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Acción
                    </label>
                    <select
                      value={bulkAction}
                      onChange={(e) => setBulkAction(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar acción...</option>
                      <option value="block_all">🔒 Bloquear completamente</option>
                      <option value="unblock_all">🔓 Desbloquear completamente</option>
                      <option value="block_teachers">👩‍🏫 Bloquear docentes</option>
                      <option value="unblock_teachers">👩‍🏫 Desbloquear docentes</option>
                      <option value="block_admins">👤 Bloquear administradores</option>
                      <option value="unblock_admins">👤 Desbloquear administradores</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Razón (opcional)
                    </label>
                    <input
                      type="text"
                      value={bulkReason}
                      onChange={(e) => setBulkReason(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Razón del cambio..."
                    />
                  </div>
                  <button
                    onClick={executeBulkAction}
                    disabled={selectedOrgs.length === 0 || !bulkAction || saving}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Aplicar a {selectedOrgs.length} seleccionados
                  </button>
                </div>
              </div>

              {/* Organizations List */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedOrgs.length === organizations.length && organizations.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedOrgs(organizations.map(o => o.tenantKey));
                            } else {
                              setSelectedOrgs([]);
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Establecimiento
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Bloqueado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Docentes
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Admins
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {organizations.map((org) => (
                      <tr key={org._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedOrgs.includes(org.tenantKey)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedOrgs([...selectedOrgs, org.tenantKey]);
                              } else {
                                setSelectedOrgs(selectedOrgs.filter(k => k !== org.tenantKey));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-gray-900">{org.displayName}</div>
                            <div className="text-sm text-gray-500">{org.tenantKey}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            org.status === 'active' ? 'bg-green-100 text-green-800' :
                            org.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {org.status === 'active' ? 'Activo' :
                             org.status === 'trial' ? 'Prueba' : 'Suspendido'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {org.accessControl?.isBlocked ? (
                            <FaLock className="w-4 h-4 text-red-500 mx-auto" />
                          ) : (
                            <FaUnlock className="w-4 h-4 text-green-500 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {org.accessControl?.blockTeachers ? (
                            <FaLock className="w-4 h-4 text-orange-500 mx-auto" />
                          ) : (
                            <FaUnlock className="w-4 h-4 text-green-500 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {org.accessControl?.blockAdmins ? (
                            <FaLock className="w-4 h-4 text-purple-500 mx-auto" />
                          ) : (
                            <FaUnlock className="w-4 h-4 text-green-500 mx-auto" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => {
                              setSelectedOrg(org);
                              setOrgSettings(org.accessControl || {
                                isBlocked: false,
                                blockTeachers: false,
                                blockAdmins: false,
                                blockedUserIds: [],
                                blockMessage: 'Su acceso ha sido temporalmente suspendido.'
                              });
                            }}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Configurar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Organization Edit Modal */}
              {selectedOrg && orgSettings && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            Control de Acceso: {selectedOrg.displayName}
                          </h3>
                          <p className="text-sm text-gray-500">{selectedOrg.tenantKey}</p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedOrg(null);
                            setOrgSettings(null);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <FaTimesCircle className="w-6 h-6" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        {/* Block All */}
                        <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FaLock className="w-5 h-5 text-red-600" />
                            <div>
                              <span className="font-medium text-red-800">Bloquear Establecimiento</span>
                              <p className="text-sm text-red-600">Bloquea todos los usuarios</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={orgSettings.isBlocked}
                              onChange={(e) => setOrgSettings({
                                ...orgSettings,
                                isBlocked: e.target.checked
                              })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                          </label>
                        </div>

                        {orgSettings.isBlocked && (
                          <div className="pl-4">
                            <input
                              type="text"
                              value={orgSettings.blockReason || ''}
                              onChange={(e) => setOrgSettings({
                                ...orgSettings,
                                blockReason: e.target.value
                              })}
                              className="w-full p-2 border border-red-300 rounded-lg"
                              placeholder="Razón del bloqueo..."
                            />
                          </div>
                        )}

                        {/* Block Teachers */}
                        <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FaChalkboardTeacher className="w-5 h-5 text-orange-600" />
                            <div>
                              <span className="font-medium text-orange-800">Bloquear Docentes</span>
                              <p className="text-sm text-orange-600">Solo docentes</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={orgSettings.blockTeachers}
                              onChange={(e) => setOrgSettings({
                                ...orgSettings,
                                blockTeachers: e.target.checked
                              })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                          </label>
                        </div>

                        {/* Block Admins */}
                        <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FaUserShield className="w-5 h-5 text-purple-600" />
                            <div>
                              <span className="font-medium text-purple-800">Bloquear Administradores</span>
                              <p className="text-sm text-purple-600">Solo administradores del establecimiento</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={orgSettings.blockAdmins}
                              onChange={(e) => setOrgSettings({
                                ...orgSettings,
                                blockAdmins: e.target.checked
                              })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
                          </label>
                        </div>

                        {/* Block Message */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mensaje para usuarios bloqueados
                          </label>
                          <textarea
                            value={orgSettings.blockMessage || ''}
                            onChange={(e) => setOrgSettings({
                              ...orgSettings,
                              blockMessage: e.target.value
                            })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            rows={2}
                            placeholder="Mensaje personalizado..."
                          />
                        </div>

                        {/* Block Expiration */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiración del bloqueo (opcional)
                          </label>
                          <input
                            type="datetime-local"
                            value={orgSettings.blockExpiresAt?.slice(0, 16) || ''}
                            onChange={(e) => setOrgSettings({
                              ...orgSettings,
                              blockExpiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined
                            })}
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                        <button
                          onClick={() => {
                            setSelectedOrg(null);
                            setOrgSettings(null);
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={saveOrgSettings}
                          disabled={saving}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                        >
                          {saving ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Guardando...</span>
                            </>
                          ) : (
                            <>
                              <FaSave className="w-4 h-4" />
                              <span>Guardar</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Historial de Cambios</h3>
              
              {history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No hay historial de cambios registrado
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((entry, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className={`p-2 rounded-full ${
                        entry.action.includes('block') && !entry.action.includes('unblock')
                          ? 'bg-red-100'
                          : 'bg-green-100'
                      }`}>
                        {entry.action.includes('block') && !entry.action.includes('unblock') ? (
                          <FaLock className={`w-4 h-4 ${
                            entry.action.includes('block') && !entry.action.includes('unblock')
                              ? 'text-red-600'
                              : 'text-green-600'
                          }`} />
                        ) : (
                          <FaUnlock className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {getActionLabel(entry.action)}
                        </div>
                        {entry.reason && (
                          <div className="text-sm text-gray-600">
                            Razón: {entry.reason}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          Por {entry.performedBy} • {formatDate(entry.performedAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessControlPanel;

